import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { ActivityEntry } from '../api/types';
import { useApi } from '../api/useApi';
import AsyncSection from '../components/AsyncSection';
import ActivityCard from '../components/sessions/ActivityCard';
import PageHeader from '../components/PageHeader';

/// Now playing: who watches what, on which device, how far along, and how it gets there. Every
/// session shows the decision behind it: which check failed, on which stream, and which encoder
/// ended up running. Films and channels alike - a channel is decided the same way, and somebody
/// watching the match is as much a session as somebody watching a film.
///
/// The server joins it all, so this page only draws. An administrator sees the whole server;
/// anybody else sees their own sessions, from the same endpoint.
export default function SessionsPage() {
  const { t } = useTranslation();

  // What is playing right now, so the page keeps up with it on its own.
  const activity = useApi(['activity'], api.activity, {
    staleTime: 0,
    refetchInterval: 5_000,
  });

  // Where a programme is moves on its own between two answers from the server.
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 5_000);

    return () => window.clearInterval(timer);
  }, []);

  const stop = (entry: ActivityEntry) => {
    // A channel viewer is closed on its own: on a shared output the others keep watching.
    const stopping = entry.kind === 'Channel' ? api.stopLiveTv(entry.sessionId) : api.stopSession(entry.sessionId);

    void stopping.then(activity.reload);
  };

  const entries = activity.data ?? [];

  return (
    <Stack spacing={3}>
      <PageHeader
        title={t('sessions.title')}
        aside={entries.length > 0 && t('sessions.activeCount', { count: entries.length })}
        actions={
          <Button onClick={activity.reload} sx={{ minHeight: 40 }}>
            {t('common.refresh')}
          </Button>
        }
      />

      <AsyncSection loading={activity.loading} error={activity.error} onRetry={activity.reload}>
        {entries.length === 0 ? (
          <Typography color="text.secondary">{t('sessions.empty')}</Typography>
        ) : (
          <Stack spacing={2}>
            {entries.map((entry) => (
              <ActivityCard key={entry.sessionId} entry={entry} now={now} onStop={() => stop(entry)} />
            ))}
          </Stack>
        )}
      </AsyncSection>
    </Stack>
  );
}
